import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  groupWaiverResults, resultLine, type WaiverResultRow,
} from '@/lib/nflWaiverResults';

/**
 * SAB-1038: the league's waiver results, run by run.
 *
 * Reads nfl_waiver_results(), which is member-gated and CANNOT return a pending row. The
 * blind-bid rule lives there rather than in a filter here: a client-side
 * `.filter(status !== 'pending')` is decoration, since anyone can call PostgREST directly
 * and a league-mate's live bid is exactly what FAAB depends on hiding.
 *
 * ── FLEX DISCIPLINE (SAB-1005) ──────────────────────────────────────────────
 *
 * A flex child's min-width defaults to `auto` — its max-content width — so `truncate` is
 * INERT until something gives that child `min-w-0`, and whichever sibling CAN shrink pays
 * for the one that cannot. Every text column here that must shrink carries `min-w-0`, the
 * bid and status chip are `shrink-0`, and exactly ONE column claims the space with
 * `flex-1`: the team name. Giving both text columns flex-1 would split the row evenly and
 * let a long reason eat half of it from the team.
 *
 * The player name is the group HEADER, not a row column, so it never competes.
 */
export default function NFLWaiverResults({ leagueId }: { leagueId: string | null }) {
  const [rows, setRows] = useState<WaiverResultRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!leagueId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await (supabase as unknown as {
        rpc: (fn: string, args: unknown) => Promise<{ data: unknown; error: { message: string } | null }>;
      }).rpc('nfl_waiver_results', { p_league_id: leagueId, p_limit: 200 });
      if (rpcErr) {
        // A refusal is not an empty log. Rendering "no results yet" for a member who was
        // denied would misreport the state of their league.
        setError(/not_a_league_member/.test(rpcErr.message)
          ? 'Waiver results are visible to league members only.'
          : 'Waiver results are unavailable right now.');
        return;
      }
      setRows((data ?? []) as WaiverResultRow[]);
    } catch {
      setError('Waiver results are unavailable right now.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => { void load(); }, [load]);

  const runs = useMemo(() => groupWaiverResults(rows ?? []), [rows]);

  return (
    <Card className="bg-card border-border" data-testid="waiver-results">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-foreground">Waiver Results</CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        {loading && !rows && (
          <p className="text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading…
          </p>
        )}
        {error && <p data-testid="waiver-results-error" className="text-xs text-muted-foreground">{error}</p>}
        {!loading && !error && runs.length === 0 && (
          <p className="text-xs text-muted-foreground">
            No waivers have processed yet. Results appear here after each nightly run.
          </p>
        )}

        {runs.map(run => (
          <div key={run.runDate} className="space-y-3">
            <p data-testid="waiver-run-header" className="text-[11px] font-display font-bold uppercase tracking-wide text-muted-foreground border-b border-border pb-1">
              {run.runDate}
            </p>
            {run.players.map(group => (
              <div key={group.playerId} data-testid="waiver-player-group" className="space-y-1">
                {/* The story's subject. min-w-0 + truncate so a long name shortens rather
                    than widening the row past the viewport. */}
                <p className="flex items-baseline gap-1.5 min-w-0">
                  <span className="min-w-0 truncate text-xs font-medium text-foreground">
                    {group.playerName}
                  </span>
                  {group.playerPosition && (
                    <span className="shrink-0 text-[10px] text-muted-foreground">{group.playerPosition}</span>
                  )}
                </p>
                {group.rows.map(row => (
                  <div
                    key={row.id}
                    data-testid="waiver-result-row"
                    className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2 pl-2 text-[11px]"
                  >
                    {/* STACKED ON NARROW, side by side from sm.
                        Measured, not assumed: side-by-side at 390 rendered the team name at
                        WIDTH 0 while page overflow read 0 and nothing was "overflowing" --
                        invisible to any truncation check, because nothing is ellipsized when
                        nothing renders (SAB-1018's shape).
                        The cause is that `flex-1` is `flex: 1 1 0%`, so the team started at
                        basis 0 while the reason's `auto` basis claimed its full content
                        width first. Both columns genuinely matter here -- who, and what
                        happened -- so per SAB-1005 they stack rather than fight for one row. */}
                    <span className={cn(
                      'min-w-0 truncate',
                      row.status === 'awarded' ? 'text-emerald-300 font-medium' : 'text-muted-foreground',
                    )}>
                      {row.team_name}
                    </span>
                    <span className="min-w-0 sm:flex-1 truncate text-muted-foreground sm:text-right">
                      {resultLine(row)}
                    </span>
                    {row.status === 'awarded' && row.resolution_priority != null && (
                      <span className="shrink-0 text-[10px] text-muted-foreground/70">
                        #{row.resolution_priority}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
